import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import { api } from '../api/client';
import { useRegisterRefresh } from '../context/PullToRefreshContext';

export default function CustomerListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('');
  const [newIsDomestic, setNewIsDomestic] = useState(null);
  const [dupWarning, setDupWarning] = useState([]);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.getCustomers(debounced || undefined);
      const list = Array.isArray(data) ? data : data.results || [];
      setCustomers(list);
    } catch {
      setError('Müşteriler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [debounced]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!newName.trim()) {
      setDupWarning([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.checkDuplicate(newName.trim());
        setDupWarning(data.duplicates || []);
      } catch {
        setDupWarning([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [newName]);

  const submitCustomer = async (e) => {
    e.preventDefault();
    setCreateError('');
    if (!newName.trim()) {
      setCreateError('Müşteri adı zorunludur.');
      return;
    }
    if (newIsDomestic === null) {
      setCreateError('Yurtiçi / Yurtdışı seçiniz.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.createCustomer({ name: newName.trim(), customer_type: newType, is_domestic: newIsDomestic });
      setShowNew(false);
      setNewName('');
      setNewType('');
      setNewIsDomestic(null);
      setDupWarning([]);
      navigate(`/satis/customers/${data.id}`);
    } catch {
      setCreateError('Kayıt oluşturulamadı.');
    } finally {
      setSaving(false);
    }
  };

  const [marketFilter, setMarketFilter] = useState('all');

  const TYPE_FILTERS = [
    { key: 'all', label: 'Tümü' },
    { key: 'sanayici', label: 'Sanayici' },
    { key: 'bayi', label: 'Bayi' },
    { key: 'boru', label: 'Boru' },
  ];

  const filteredCustomers = customers
    .filter(c => typeFilter === 'all' || c.customer_type === typeFilter)
    .filter(c => {
      if (marketFilter === 'domestic') return c.is_domestic === true;
      if (marketFilter === 'foreign') return c.is_domestic === false;
      return true;
    });

  return (
    <div className="px-4 py-3 pb-24 space-y-4">
      <input
        type="text"
        placeholder="İsme göre ara…"
        className="input"
        style={{ fontSize: '16px' }}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* Piyasa filtresi */}
      <div className="flex gap-1.5">
        {[
          { key: 'all', label: 'Tümü' },
          { key: 'domestic', label: 'Yurtiçi' },
          { key: 'foreign', label: 'Yurtdışı' },
        ].map(f => (
          <button
            key={f.key}
            type="button"
            onClick={() => setMarketFilter(f.key)}
            className={`whitespace-nowrap px-3 py-1 rounded-full text-xs font-medium border transition-colors shrink-0
              ${marketFilter === f.key
                ? 'bg-slate-600 text-white border-slate-600'
                : 'bg-white text-gray-500 border-gray-200'
              }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Tür filtresi */}
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
        {TYPE_FILTERS.map(f => (
          <button
            key={f.key}
            type="button"
            onClick={() => setTypeFilter(f.key)}
            className={`whitespace-nowrap px-3 py-1 rounded-full text-xs font-medium border transition-colors shrink-0
              ${typeFilter === f.key
                ? 'bg-brand-500 text-white border-brand-500'
                : 'bg-white text-gray-500 border-gray-200'
              }`}
          >
            {f.label}
            {' '}<span className="opacity-70">
              ({f.key === 'all' ? customers.length : customers.filter(c => c.customer_type === f.key).length})
            </span>
          </button>
        ))}
      </div>

      <button type="button" className="btn-primary" onClick={() => setShowNew(true)}>
        + Yeni Müşteri
      </button>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl p-3">{error}</div>
      )}

      {loading && (
        <div className="flex justify-center py-12">
          <div className="w-7 h-7 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!loading && filteredCustomers.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => navigate(`/satis/customers/${c.id}`)}
          className="card w-full text-left active:scale-[0.99] transition-transform"
        >
          <div className="flex items-center gap-2">
            <p className="font-semibold text-gray-900 flex-1">{c.name}</p>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border shrink-0
              ${c.is_domestic === false
                ? 'bg-sky-50 text-sky-700 border-sky-200'
                : 'bg-gray-50 text-gray-600 border-gray-200'
              }`}
            >
              {c.is_domestic === false ? 'Yurtdışı' : 'Yurtiçi'}
            </span>
            {c.customer_type && (
              <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 border border-brand-200 shrink-0">
                {c.customer_type}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Açık iş: <span className="text-brand-600 font-medium">{c.open_task_count ?? 0}</span>
          </p>
          {c.updated_at && (
            <p className="text-xs text-gray-400 mt-1">
              Güncelleme:{' '}
              {format(parseISO(c.updated_at), 'd MMMM yyyy HH:mm', { locale: tr })}
            </p>
          )}
        </button>
      ))}

      {!loading && filteredCustomers.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-8">Kayıt bulunamadı.</p>
      )}

      {showNew && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 sm:items-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-4 space-y-3">
            <h3 className="font-semibold text-gray-900">Yeni müşteri</h3>
            <form onSubmit={submitCustomer} className="space-y-3">
              {createError && <p className="text-sm text-red-600">{createError}</p>}
              <div>
                <p className="text-xs font-medium text-gray-700 mb-1.5">Piyasa <span className="text-red-500">*</span></p>
                <div className="flex rounded-xl border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setNewIsDomestic(true)}
                    className={`flex-1 py-2.5 px-2 text-sm font-medium border-r border-gray-200 transition-colors
                      ${newIsDomestic === true ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >
                    Yurtiçi
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewIsDomestic(false)}
                    className={`flex-1 py-2.5 px-2 text-sm font-medium transition-colors
                      ${newIsDomestic === false ? 'bg-brand-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  >
                    Yurtdışı
                  </button>
                </div>
              </div>
              <input
                className="input"
                style={{ fontSize: '16px' }}
                placeholder="Müşteri adı"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                autoFocus
              />
              <select
                className="input"
                style={{ fontSize: '16px' }}
                value={newType}
                onChange={(e) => setNewType(e.target.value)}
              >
                <option value="">Müşteri türü seçin (opsiyonel)</option>
                <option value="sanayici">Sanayici</option>
                <option value="boru">Boru</option>
                <option value="bayi">Bayi</option>
              </select>
              {dupWarning.length > 0 && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-xl p-3">
                  Benzer kayıtlar: {dupWarning.map((d) => d.name).join(', ')}
                </div>
              )}
              <div className="flex gap-2">
                <button type="button" className="btn-secondary" onClick={() => { setShowNew(false); setNewName(''); setNewType(''); setNewIsDomestic(null); }}>
                  Vazgeç
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Kaydediliyor…' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
